import React, { useMemo } from 'react';
import { Col, Row } from 'react-bootstrap';
import SavedFilterItem from './SavedFilterItem';
import type { SavedSearchFilter } from '../../types';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '../../../../../store';
import { post } from '../../../../../api/baseUrl';
import { SystemPermission } from '../../../../../context';
import {
  setCurrentFilter,
  setUserSavedFilters,
} from '../../../../../store/ai2';
import { convertToFilterString, sanitizeSearchFilterTokens } from '../../utils';

export enum UpdateUserFilterAction {
  PIN = 'pin',
  DELETE = 'delete',
}

interface SavedFiltersProps {
  savedSearchFilters: SavedSearchFilter[];
  showSavedSearch: boolean;
  onFocusOnSearch: boolean;
  toggleSavedSearch: () => void;
  onSelectSavedFilter: (id: string) => void;
}
const SavedFilters = ({
  savedSearchFilters,
  showSavedSearch,
  onFocusOnSearch,
  toggleSavedSearch,
  onSelectSavedFilter,
}: SavedFiltersProps) => {
  const [isPending, setIsPending] = React.useState(false);
  const dispatch = useDispatch();
  const { selectedTenancy } = useSelector(
    (state: RootState) => state.clinicianProfile,
  );
  const searchFiltersMap = selectedTenancy?.features?.searchFiltersMap || {};
  const canAccessOpenSafetyAlerts =
    !selectedTenancy?.permissionsSetup ||
    !!selectedTenancy?.userPermissions?.[SystemPermission.SAFETY_MONITORING];
  const canAccessReadyForReview =
    !selectedTenancy?.permissionsSetup ||
    !selectedTenancy?.userPermissions?.[SystemPermission.DEIDENTIFIED_DATA];

  const visibleSavedSearchFilters = useMemo(() => {
    return savedSearchFilters.filter((item) => {
      const filters = convertToFilterString(item, searchFiltersMap);
      const sanitizedFilters = sanitizeSearchFilterTokens(
        filters,
        searchFiltersMap,
        {
          allowOpenSafetyAlerts: canAccessOpenSafetyAlerts,
          allowReadyForReview: canAccessReadyForReview,
        },
      );

      return (
        sanitizedFilters.length === filters.length &&
        sanitizedFilters.every((filter, index) => filter === filters[index])
      );
    });
  }, [
    canAccessOpenSafetyAlerts,
    canAccessReadyForReview,
    savedSearchFilters,
    searchFiltersMap,
  ]);

  const onClickFilter = (id: string) => {
    onSelectSavedFilter(id);
  };

  const pinnedFilters = async (id: string) => {
    if (!selectedTenancy) return;
    try {
      setIsPending(true);
      return await post('/updateUserFilter', {
        tenancyId: selectedTenancy.tenancy_id,
        id,
        action: UpdateUserFilterAction.PIN,
      });
    } catch (error) {
      console.error(error);
      return null;
    } finally {
      setIsPending(false);
    }
  };

  const onPinClick = async (id: string) => {
    const originalfilters = [...savedSearchFilters];
    const pinnedFilter = savedSearchFilters.find((item) => item.id === id);
    if (pinnedFilter) {
      const updatedFilters = savedSearchFilters.map((item) => {
        return { ...item, pinned: false };
      });
      const updatedPinnedFilter = {
        ...pinnedFilter,
        pinned: !pinnedFilter.pinned,
      };
      const sortedFilters = [
        updatedPinnedFilter,
        ...updatedFilters.filter((item) => item.id !== id),
      ].sort((a, b) => (a.pinned === b.pinned ? 0 : a.pinned ? -1 : 1));
      const currentPinnedFilter = visibleSavedSearchFilters.find(
        (item) => item.pinned && item.id !== id,
      );
      if (!currentPinnedFilter) {
        dispatch(setCurrentFilter([]));
      }
      dispatch(setUserSavedFilters(sortedFilters));

      const result = await pinnedFilters(pinnedFilter.id);
      if (!result) {
        dispatch(setUserSavedFilters(originalfilters));
      }
    }
  };

  const deleteFilter = async (id: string) => {
    if (!selectedTenancy) return;
    try {
      setIsPending(true);
      return await post('/updateUserFilter', {
        tenancyId: selectedTenancy.tenancy_id,
        id,
        action: UpdateUserFilterAction.DELETE,
      });
    } catch (error) {
      console.error(error);
      return null;
    } finally {
      setIsPending(false);
    }
  };

  const onTrashClick = async (id: string) => {
    const originalfilters = [...savedSearchFilters];
    const updatedFilters = savedSearchFilters.filter((item) => item.id !== id);
    dispatch(setUserSavedFilters(updatedFilters));
    const result = await deleteFilter(id);
    if (!result) {
      dispatch(setUserSavedFilters(originalfilters));
    }
  };

  return (
    <div className="search-box-saved-field">
      {visibleSavedSearchFilters.length > 0 && (
        <div className="mt-1">
          <button onClick={toggleSavedSearch} disabled={isPending}>
            {showSavedSearch ? 'Hide' : 'See'} my saved searches
          </button>
        </div>
      )}
      {visibleSavedSearchFilters.length > 0 &&
        showSavedSearch &&
        !onFocusOnSearch && (
          <Row>
            <Col sm={10} className="saved-items-container">
              <div className="bg position-relative">
                {visibleSavedSearchFilters.map((item) => {
                  return (
                    <SavedFilterItem
                      id={item.id}
                      key={item.id}
                      text={item.text}
                      pinned={item.pinned}
                      onClick={onClickFilter}
                      onPinClick={onPinClick}
                      onTrashClick={onTrashClick}
                    />
                  );
                })}
              </div>
            </Col>
          </Row>
        )}
    </div>
  );
};

export default SavedFilters;
